import { ReactNode } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePhotoExpiration } from '../../hooks/usePhotoExpiration';
import { PhotoExpirationBanner } from '../diagnosis/PhotoExpirationBanner';

interface AppLayoutProps {
  children: ReactNode;
}

/**
 * App layout wrapper that includes global components like photo expiration warnings.
 * The banner appears at the top of all pages when photos are expiring.
 */
export function AppLayout({ children }: AppLayoutProps) {
  const { user } = useAuth();
  const {
    expiringPhotos,
    loading,
    isDismissed,
    dismiss,
    extendAll,
    extending,
  } = usePhotoExpiration(user?.uid ?? null);

  // Calculate minimum days remaining across all expiring photos
  const minDaysRemaining = expiringPhotos.length > 0
    ? Math.min(...expiringPhotos.map((ep) => ep.daysRemaining))
    : 0;

  const showBanner =
    !loading &&
    !isDismissed &&
    expiringPhotos.length > 0 &&
    minDaysRemaining <= 7;

  const handleKeepPhotos = async () => {
    try {
      await extendAll();
    } catch (error) {
      // Error is logged in the hook, user sees "Extending..." revert to button
      console.error('Failed to extend photos:', error);
    }
  };

  return (
    <>
      {/* Skip navigation link - visually hidden until focused */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-white focus:shadow-elevated focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-light"
      >
        Skip to main content
      </a>
      {showBanner && (
        <PhotoExpirationBanner
          photoCount={expiringPhotos.length}
          daysRemaining={minDaysRemaining}
          onKeepPhotos={handleKeepPhotos}
          onDismiss={dismiss}
          extending={extending}
        />
      )}
      <div id="main-content" tabIndex={-1} className="outline-none">
        {children}
      </div>
    </>
  );
}
